import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import CalendarTodayRounded from "@mui/icons-material/CalendarTodayRounded";
import { AssessmentOutlined, SettingsOutlined, CalendarMonthOutlined, DnsOutlined, ArrowForwardRounded, RefreshRounded, GroupsOutlined, SwapHorizRounded, StorageOutlined } from "@mui/icons-material";
import { designColors, designFontFamily, designRadii, designShell, designTheme, designType } from "services/constants/designTokens";
import { GlobalBreadcrumb, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { EmptyState } from "components/CustomComponents/EmptyState";
import { customGetAPI } from "services/utils/CustomAPI";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { tooltipStyleProps } from "services/constants/Styles";
import { sectionCardSx, sectionTitleSx, SectionHeader } from "pages/TeamZone/Home/homeUi";
import { dashboardUrl, mapDashboard } from "./dashboardData";
import { dashboardTraceUrl, dashboardCardFilters, dashboardRowFilters, recentIssueFilters } from "./dashboardLinks";
const colors = {
  success: "#059669",
  warning: "#D97706",
  error: "#EF4444",
  info: "#3B82F6"
};
// Same "Data as of" stamp as Team Workspace Home.
const formatAsOf = value => (value && dayjs(value).isValid() ? dayjs(value) : dayjs()).format("ddd, DD MMM YYYY hh:mm A");
const compactEmptySx = { py: 2, minHeight: 0 };
const recentIssuesLimit = 5;
const deviceHealthLimit = 4;
const statusColor = status => colors[["Healthy", "Online"].includes(status) ? "success" : ["Failed", "Offline", "Quarantined"].includes(status) ? "error" : "warning"];
// Icon tile colours, one per card / stage, from the same accent + tint pairs
// Team Workspace Home's stat cards use.
const iconTones = [
  { color: "#2563EB", bg: "#EFF6FF" },
  { color: "#F97316", bg: "#FFF7ED" },
  { color: "#16A34A", bg: "#ECFDF3" },
  { color: "#7C3AED", bg: "#F5F3FF" },
  { color: "#0F766E", bg: "#E6F8F4" }
];
const icons = [AssessmentOutlined, SettingsOutlined, CalendarMonthOutlined, DnsOutlined];
const flowIcons = [AssessmentOutlined, GroupsOutlined, SwapHorizRounded, StorageOutlined, CalendarMonthOutlined];
const Status = ({
  value
}) => <RequestStatusChip status={value} colorCode={statusColor(value)} sx={{
  maxWidth: "100%"
}} />;
// Placeholders shown while the dashboard API call is in flight. The whole
// card is a skeleton, header included, shaped like the content it stands
// in for (same approach as Team Workspace Home).
const pillSkeletonSx = { borderRadius: 999 };
function SummaryCardSkeleton() {
  return <Mui.Box data-testid="monitoring-skeleton">
    <Mui.Stack direction="row" spacing={1.5} alignItems="center"><Mui.Skeleton variant="rounded" width={46} height={46} /><Mui.Box sx={{
        flex: 1
      }}><Mui.Skeleton variant="text" width="55%" height={24} /><Mui.Skeleton variant="text" width="85%" height={18} /></Mui.Box><Mui.Skeleton variant="circular" width={24} height={24} /></Mui.Stack>
    <Mui.Stack direction="row" alignItems="center" spacing={1} sx={{
      my: 1.7
    }}><Mui.Skeleton variant="text" width={56} height={39} /><Mui.Skeleton variant="text" width={110} height={20} /></Mui.Stack>
    <Mui.Skeleton variant="rounded" height={7} sx={{
      borderRadius: 5,
      mb: 1.5
    }} />
    <Mui.Stack spacing={1}>{[0, 1, 2].map(row => <Mui.Stack key={row} direction="row" spacing={1} alignItems="center"><Mui.Skeleton variant="circular" width={8} height={8} /><Mui.Skeleton variant="text" height={20} sx={{
          flex: 1
        }} /><Mui.Skeleton variant="text" width={24} height={20} /></Mui.Stack>)}</Mui.Stack>
  </Mui.Box>;
}
function SkeletonRows({
  rows
}) {
  return <Mui.Stack spacing={1.5} data-testid="monitoring-skeleton" sx={{
    py: 0.5
  }}>{Array.from({
      length: rows
    }, (_, row) => <Mui.Stack key={row} direction="row" spacing={2} alignItems="center" justifyContent="space-between"><Mui.Box sx={{
        flex: 1
      }}><Mui.Skeleton variant="text" width="60%" height={22} /><Mui.Skeleton variant="text" width="40%" height={18} /></Mui.Box><Mui.Skeleton variant="rounded" width={84} height={24} sx={pillSkeletonSx} /></Mui.Stack>)}</Mui.Stack>;
}
function Section({
  title,
  tooltip,
  action,
  onAction,
  children,
  sx,
  loading
}) {
  return <Mui.Box component="section" sx={{
    ...sectionCardSx,
    minWidth: 0,
    ...sx
  }}>
    <Mui.Box sx={{
      mb: 2
    }}>{loading ? <Mui.Stack direction="row" alignItems="center" justifyContent="space-between"><Mui.Skeleton variant="text" width={170} height={26} />{action && <Mui.Skeleton variant="text" width={90} height={22} />}</Mui.Stack> : <SectionHeader title={title} tooltip={tooltip} actionLabel={action} onAction={onAction} />}</Mui.Box>
    {children}
  </Mui.Box>;
}
function DataTable({
  headers,
  rows
}) {
  return <Mui.TableContainer><Mui.Table size="small">
    <Mui.TableHead><Mui.TableRow>{headers.map(header => <Mui.TableCell key={header} scope="col" align="left" sx={{
            // Compact dashboard tables use the shared non-sticky header base.
            ...designShell.tableHeadCellBase,
            py: 0.75,
            px: 1,
            whiteSpace: "nowrap"
          }}>{header}</Mui.TableCell>)}</Mui.TableRow></Mui.TableHead>
    <Mui.TableBody>{rows.map((row, index) => <Mui.TableRow key={index}>{row.map((cell, column) => <Mui.TableCell key={column} sx={{
            ...sharedTableBodyCellSx,
            px: 1,
            py: 1.2
          }}>{cell}</Mui.TableCell>)}</Mui.TableRow>)}</Mui.TableBody>
  </Mui.Table></Mui.TableContainer>;
}
export function MonitoringDashboard() {
  const history = useHistory();
  const { domain } = useSelector(state => state.tokenReducer);
  // Same date control as Team Workspace Home: click the stamp to pick a day.
  // Not persisted, so the dashboard opens on today. The picked day is sent
  // as both start_date and end_date.
  const [selectedDate, setSelectedDate] = useState(() => dayjs());
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const dateKey = selectedDate.format("YYYY-MM-DD");
  useEffect(() => {
    if (!domain) return undefined;
    let active = true;
    setLoading(true);
    customGetAPI(dashboardUrl(domain, dateKey)).then(result => {
      if (!active) return;
      // customGetAPI already shows the error toast; keep the last good data.
      if (result?.ok) setResponse(result.responseDetails);
      setFailed(!result?.ok);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [domain, dateKey, reloadKey]);
  const { summary, stages, issues, jobs, devices, lastUpdated } = useMemo(() => mapDashboard(response, dateKey), [response, dateKey]);
  const asOf = formatAsOf(lastUpdated);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const isSelectedDateToday = selectedDate.isSame(dayjs(), "day");
  const handleDateChange = newValue => {
    if (newValue && newValue.isValid() && !newValue.isAfter(dayjs(), "day")) {
      setSelectedDate(newValue);
    }
    setDatePickerAnchor(null);
  };
  const [panel, setPanel] = useState(null);
  const openTrace = (filters = {}) => history.push(dashboardTraceUrl(response?.start_date || dateKey, response?.end_date || dateKey, filters));
  const openIssues = () => openTrace();
  const issueRows = issues.map(issue => [issue.time, <Mui.Button key={issue.id} onClick={() => setPanel({
    title: `${issue.name} · ${issue.id}`,
    record: issue
  })} sx={{
    p: 0,
    textAlign: "left",
    display: "block",
    textTransform: "none",
    color: designColors.slate800,
    fontSize: 14,
    fontWeight: 500
  }}>{issue.name}<Mui.Typography sx={{
      fontSize: 13,
      color: designColors.slate600
    }}>{issue.id}</Mui.Typography></Mui.Button>, issue.stage, <Status key="status" value={issue.status} />]);
  const jobRows = jobs.map(job => [job.name, <Status key="status" value={job.status} />, job.queue]);
  return <Mui.ThemeProvider theme={designTheme}>
    <Mui.Box sx={{
      ...designShell.pageSurface,
      fontFamily: designFontFamily,
      p: {
        xs: 2,
        md: 3
      }
    }}>
      <Mui.Stack spacing={2} aria-busy={loading}>
        <GlobalBreadcrumb trail={[{
          label: "Monitoring"
        }, {
          label: "Dashboard"
        }]} />
        <Mui.Stack direction={{
          xs: "column",
          md: "row"
        }} justifyContent="space-between" spacing={2}>
          <Mui.Box><Mui.Typography component="h1" sx={designType.pageTitle}>Monitoring Dashboard</Mui.Typography><Mui.Typography sx={designType.pageSubtitle}>Overview of biometric event processing and device health.</Mui.Typography></Mui.Box>
          <Mui.Stack direction="row" alignItems="center" spacing={1.5}>
            <Mui.Stack direction="row" alignItems="center" spacing={0.6} role="button" tabIndex={0} aria-label="Change date" onClick={event => setDatePickerAnchor(event.currentTarget)} onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") setDatePickerAnchor(event.currentTarget);
            }} sx={{
              cursor: "pointer",
              px: 1,
              py: 0.5,
              borderRadius: designRadii.control,
              border: "1px solid transparent",
              "&:hover": {
                backgroundColor: designColors.slateBg,
                border: `1px solid ${designColors.slate200}`
              }
            }}><CalendarTodayRounded sx={{
                fontSize: 15,
                color: designColors.tealDark
              }} /><Mui.Typography aria-live="polite" sx={{
                fontSize: 13.5,
                fontWeight: 600,
                color: designColors.slate700
              }}>{isSelectedDateToday ? `Data as of: ${asOf}` : `Showing data for: ${selectedDate.format("ddd, DD MMM YYYY")}`}</Mui.Typography></Mui.Stack>
            <Mui.Popover open={Boolean(datePickerAnchor)} anchorEl={datePickerAnchor} onClose={() => setDatePickerAnchor(null)} anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }} transformOrigin={{
              vertical: "top",
              horizontal: "left"
            }}><LocalizationProvider dateAdapter={AdapterDayjs}><StaticDatePicker value={selectedDate} onChange={handleDateChange} disableFuture displayStaticWrapperAs="desktop" slotProps={{
                  actionBar: {
                    actions: []
                  }
                }} /></LocalizationProvider></Mui.Popover>
            <Mui.Button variant="outlined" startIcon={<RefreshRounded />} disabled={loading} onClick={() => {
              setSelectedDate(dayjs());
              setReloadKey(key => key + 1);
            }} sx={{
              ...designShell.compactOutlineButton,
              px: 1.75
            }}>Refresh</Mui.Button>
          </Mui.Stack>
        </Mui.Stack>
        {failed && !loading && <Mui.Alert severity="error" sx={{
          py: 0,
          borderRadius: 2
        }}>Monitoring data could not be loaded. Use Refresh to try again.</Mui.Alert>}
        <Mui.Box sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            xl: "repeat(4, 1fr)"
          },
          gap: 2
        }}>
          {summary.map((card, index) => {
            const Icon = icons[index];
            const total = card.rows.reduce((sum, row) => sum + row[1], 0);
            return <Mui.Box key={card.title} sx={{
              ...sectionCardSx
            }}>
              {loading ? <SummaryCardSkeleton /> : <>
              <Mui.Stack direction="row" spacing={1.5} alignItems="center">
                <Mui.Avatar variant="rounded" sx={{
                  bgcolor: iconTones[index].bg,
                  color: iconTones[index].color,
                  width: 46,
                  height: 46
                }}><Icon /></Mui.Avatar>
                <Mui.Box sx={{
                  flex: 1
                }}><Mui.Typography component="h2" sx={sectionTitleSx}>{card.title}</Mui.Typography><Mui.Typography sx={designType.helperText}>{card.description}</Mui.Typography></Mui.Box>
                {dashboardCardFilters[card.title] ? <Mui.Tooltip title="View events visible to your tenant" arrow placement="top" describeChild slotProps={tooltipStyleProps}><Mui.IconButton aria-label={`View ${card.title} events`} onClick={() => openTrace(dashboardCardFilters[card.title])}><ArrowForwardRounded sx={{ fontSize: 18 }} /></Mui.IconButton></Mui.Tooltip> : <ArrowForwardRounded aria-hidden data-testid="summary-card-arrow" sx={{ fontSize: 18, color: designColors.slate500 }} />}
              </Mui.Stack>
              <Mui.Stack direction="row" alignItems="center" spacing={1} sx={{
                my: 1.7
              }}><Mui.Typography sx={{
                  fontSize: 26,
                  fontWeight: 700,
                  color: designColors.slate900
                }}>{card.value}</Mui.Typography><Mui.Typography sx={{
                  fontSize: 13,
                  color: designColors.slate600,
                  flex: 1
                }}>{card.label}</Mui.Typography></Mui.Stack>
              <Mui.Box aria-hidden="true" sx={{
                display: "flex",
                height: 7,
                borderRadius: 5,
                overflow: "hidden",
                mb: 1.5,
                bgcolor: designColors.slate100
              }}>{total > 0 && card.rows.map(([label, value, color]) => <Mui.Box key={label} sx={{
                  width: `${value / total * 100}%`,
                  bgcolor: colors[color],
                  borderRight: "1px solid white"
                }} />)}</Mui.Box>
              <Mui.Stack spacing={1}>{card.rows.map(([label, value, color]) => {
                const rowFilter = dashboardRowFilters[card.title]?.[label];
                // Shared tooltip (not the browser's native title box).
                const rowTip = rowFilter && (card.title === "Attendance" ? "Dashboard counts employee-days; Event Trace shows events visible to your tenant." : "View events visible to your tenant");
                const row = <Mui.Stack key={label} component={rowFilter ? Mui.ButtonBase : "div"} onClick={rowFilter ? () => openTrace(rowFilter) : undefined} aria-label={rowFilter ? `${card.title} ${label}: ${value}` : undefined} direction="row" spacing={1} alignItems="center" sx={{ width: "100%", textAlign: "left" }}><Mui.Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: colors[color]
                  }} /><Mui.Typography sx={{
                    // Breakdown rows sit below the card title in weight and
                    // size (same as Home's shift coverage rows).
                    flex: 1,
                    fontSize: 13.5,
                    fontWeight: 400,
                    color: designColors.slate700
                  }}>{label}</Mui.Typography><Mui.Typography sx={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: designColors.slate900
                  }}>{value}</Mui.Typography></Mui.Stack>;
                return rowTip ? <Mui.Tooltip key={label} title={rowTip} arrow placement="top" describeChild slotProps={tooltipStyleProps}>{row}</Mui.Tooltip> : row;
              })}</Mui.Stack>
              </>}
            </Mui.Box>;
          })}
        </Mui.Box>
        <Section loading={loading} title="Processing Flow Health" tooltip="Current status of the punch to attendance processing pipeline." action="View Event Trace" onAction={openIssues}>
          <Mui.Box sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(5, 1fr)"
            },
            gap: 2
          }}>
            {stages.map((stage, index) => {
              const Icon = flowIcons[index];
              return <Mui.Box key={stage.name} sx={{
                position: "relative",
                p: 1
              }}>
              <Mui.Stack direction="row" spacing={1.2} alignItems="center">{loading ? <Mui.Skeleton variant="circular" width={46} height={46} /> : <Mui.Avatar sx={{
                    bgcolor: iconTones[index].bg,
                    color: iconTones[index].color,
                    width: 46,
                    height: 46
                  }}><Icon /></Mui.Avatar>}<Mui.Box>{loading ? <Mui.Skeleton variant="text" width={110} height={22} sx={{
                      mb: 0.7
                    }} /> : <Mui.Typography sx={{
                      fontSize: 14.5,
                      fontWeight: 500,
                      color: designColors.slate800,
                      mb: 0.7
                    }}>{stage.name}</Mui.Typography>}{loading ? <Mui.Skeleton data-testid="monitoring-skeleton" variant="rounded" width={72} height={24} sx={pillSkeletonSx} /> : <Status value={stage.status} />}</Mui.Box></Mui.Stack>
              <Mui.Box sx={{
                  ml: 7,
                  mt: 1.5
                }}>{loading ? <><Mui.Skeleton variant="text" width={48} height={27} /><Mui.Skeleton variant="text" width={80} height={20} /></> : <><Mui.Typography sx={{
                    fontWeight: 700,
                    fontSize: 18,
                    color: designColors.slate900
                  }}>{stage.count}</Mui.Typography><Mui.Typography sx={{
                    fontSize: 13,
                    color: designColors.slate600
                  }}>{stage.label}</Mui.Typography>{stage.extra && <Mui.Typography sx={{
                    fontSize: 13,
                    color: stage.extraTone === "warning" ? colors.warning : designColors.slate600,
                    mt: 0.5
                  }}>{stage.extra}</Mui.Typography>}</>}</Mui.Box>
              {index < 4 && !loading && <ArrowForwardRounded sx={{
                  display: {
                    xs: "none",
                    lg: "block"
                  },
                  position: "absolute",
                  right: -16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: designColors.slate400,
                  fontSize: 19
                }} />}
            </Mui.Box>;
            })}
          </Mui.Box>
        </Section>
        <Mui.Box sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr",
            lg: "1.3fr 1fr 1fr"
          },
          gap: 2,
          alignItems: "stretch"
        }}>
          <Section loading={loading} title="Recent Issues" tooltip="Latest events that need a retry or were quarantined." action={issues.length > recentIssuesLimit ? "View all" : undefined} onAction={() => openTrace(recentIssueFilters)} sx={{ gridColumn: { md: "1 / -1", lg: "auto" } }}>{loading ? <SkeletonRows rows={recentIssuesLimit} /> : issueRows.length ? <DataTable headers={["Time", "Employee / Device", "Stage", "Issue"]} rows={issueRows.slice(0, recentIssuesLimit)} /> : <EmptyState title="No Recent Issues" subtitle="Nothing needs a retry or was quarantined in this period." sx={compactEmptySx} size={96} />}</Section>
            <Section loading={loading} title="Job Health" tooltip="Status and queue size of each background processing job." action="View logs" onAction={() => setPanel({
              title: "Job Health",
              type: "jobs"
            })}>{loading ? <SkeletonRows rows={4} /> : <DataTable headers={["Job", "Status", "Queue"]} rows={jobRows} />}</Section>
            <Section loading={loading} title="Device Health" tooltip="Devices that are offline or have no recent punches." action={devices.length > deviceHealthLimit ? "View all" : undefined} onAction={() => history.push("/admin/monitoring/devices")}>
              {loading && <SkeletonRows rows={deviceHealthLimit} />}
              {!loading && !devices.length && <EmptyState title="No Devices To Review" subtitle="No device health records were returned." sx={compactEmptySx} size={96} />}
              <Mui.Stack spacing={0.5}>{(loading ? [] : devices.slice(0, deviceHealthLimit)).map(device => <Mui.Button key={device.id} onClick={() => history.push(`/admin/monitoring/devices?device=${encodeURIComponent(device.id)}`)} sx={{
                  textTransform: "none",
                  justifyContent: "space-between",
                  color: designColors.slate700,
                  p: 0.7,
                  gap: 1,
                  textAlign: "left"
                }}><Mui.Box><Mui.Typography sx={{
                      fontSize: 14.5,
                      fontWeight: 500,
                      color: designColors.slate800
                    }}>{device.name} ({device.id})</Mui.Typography><Mui.Typography sx={{
                      fontSize: 13,
                      color: designColors.slate600
                    }}>Last seen: {device.lastSeen}</Mui.Typography><Mui.Typography sx={{
                      fontSize: 13,
                      color: designColors.slate600
                    }}>Last punch: {device.lastPunch}</Mui.Typography></Mui.Box><Status value={device.status} /></Mui.Button>)}</Mui.Stack>
            </Section>
        </Mui.Box>
      </Mui.Stack>
    </Mui.Box>
    <GlobalSidePanel open={Boolean(panel)} onClose={() => setPanel(null)} title={panel?.title} bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
      {panel?.type === "issues" && <DataTable headers={["Time", "Employee / Device", "Stage", "Issue"]} rows={issueRows} />}
      {panel?.type === "jobs" && <DataTable headers={["Job", "Status", "Current queue"]} rows={jobRows} />}
      {panel?.record && <DataTable headers={["Field", "Value"]} rows={Object.entries(panel.record).map(([key, value]) => [key === "lastSeen" ? "Last seen" : key === "lastPunch" ? "Last punch" : key.charAt(0).toUpperCase() + key.slice(1), key === "status" ? <Status key="status" value={value} /> : value])} />}
    </GlobalSidePanel>
  </Mui.ThemeProvider>;
}

// Read-only screen: only the view permission applies.
export default withScreenPermissions(MonitoringDashboard, SCREEN_ACCESS.monitoringDashboard, "Monitoring Dashboard");
